import { useQueries, useQuery, UseQueryOptions, UseQueryResult } from 'react-query';
import {
  IntegrationsAuthAPI,
  ListSourcesRequest,
  ListSourcesResponse,
} from '@weave/schema-gen-ts/dist/schemas/integrations/integrations-auth-api/v1/service.pb';
import { SyncConfigAPI } from '@weave/schema-gen-ts/dist/schemas/integrations/sync-config-api/v1/api.pb';
import { SourceContainerConfig } from '@weave/schema-gen-ts/dist/schemas/integrations/sync-config-api/v1/message.pb';
import {
  PingResponse,
  RestartResponse,
  StatusResponse,
  SyncCommandControlService,
} from '@weave/schema-gen-ts/dist/schemas/integrations/sync-command-control/v1/service.pb';
import { authorizedFetch } from 'helpers/utils';

const CACHE_TIME = 1000 * 60 * 60; // 1 hour

export const SyncEngineSourceQueryKeys = {
  GET_SYNC_ENGINE_SOURCES: 'sync-engine-sources',
  GET_SOURCE_CONTAINER_CONFIG: 'sync-config-source-container-config',
};

export const SyncCommandControlQueryKeys = {
  PING: 'sync-command-control-ping',
  RESTART: 'sync-command-control-restart',
  STATUS: 'sync-command-control-status',
};

// sync-config-api returns snake_case JSON while the generated PB types are
// camelCase. Normalize at the boundary so consumers can safely use the typed
// camelCase fields (e.g. `syncEngines`, `pmsName`).
type JsonValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | JsonValue[]
  | { [key: string]: JsonValue };

const snakeToCamel = (str: string): string =>
  str.replace(/_([a-z0-9])/g, (_match, ch: string) => ch.toUpperCase());

function keysToCamel<T extends JsonValue>(value: T): T;
function keysToCamel(value: JsonValue): JsonValue {
  if (Array.isArray(value)) {
    return value.map((item) => keysToCamel(item));
  }
  if (value !== null && typeof value === 'object') {
    return Object.entries(value).reduce<{ [key: string]: JsonValue }>(
      (acc, [key, val]) => {
        acc[snakeToCamel(key)] = keysToCamel(val);
        return acc;
      },
      {}
    );
  }
  return value;
}

const useGetSyncEngineSources = <T = ListSourcesResponse>(
  request: ListSourcesRequest,
  options?: UseQueryOptions<ListSourcesResponse, Error, T>
) => {
  return useQuery({
    queryKey: [SyncEngineSourceQueryKeys.GET_SYNC_ENGINE_SOURCES, request],
    queryFn: () =>
      IntegrationsAuthAPI.ListSources(
        authorizedFetch({ httpOptions: { params: { pageSize: 100 } } }),
        request
      ),
    retry: false,
    enabled: true,
    retryOnMount: true,
    refetchOnMount: 'always',
    cacheTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    ...options,
  });
};

// Source container configs are owned by sync-config-api, which is the source of
// truth for a source's PM (Practice Management) details. Each source_id maps to
// exactly one config, so we fetch them individually via the GET endpoint — one
// parallel query per source id.
const useGetSourceContainerConfigs = (sourceIds: string[]) =>
  useQueries(
    sourceIds.map((sourceId) => ({
      queryKey: [SyncEngineSourceQueryKeys.GET_SOURCE_CONTAINER_CONFIG, sourceId],
      queryFn: async () => {
        const raw = await SyncConfigAPI.GetSourceContainerConfig(authorizedFetch(), {
          sourceId,
        });
        return keysToCamel(raw) as SourceContainerConfig;
      },
      retry: false,
      enabled: !!sourceId,
      retryOnMount: true,
      refetchOnMount: 'always' as const,
      cacheTime: CACHE_TIME,
      staleTime: CACHE_TIME,
    }))
  ) as UseQueryResult<SourceContainerConfig, Error>[];

const usePingSyncCommandControl = (
  sourceId: string,
  engineId: string,
  options?: UseQueryOptions<PingResponse, Error>
) => {
  return useQuery<PingResponse, Error>({
    queryKey: [SyncCommandControlQueryKeys.PING, sourceId, engineId],
    queryFn: () =>
      SyncCommandControlService.Ping(authorizedFetch(), {
        sourceId: sourceId,
        engineId: engineId,
      }),
    retry: false,
    enabled: false,
    retryOnMount: false,
    refetchOnMount: false,
    cacheTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    ...options,
  });
};

const useRestartSyncCommandControl = (
  sourceId: string,
  engineId: string,
  options?: UseQueryOptions<RestartResponse, Error>
) => {
  return useQuery<RestartResponse, Error>({
    queryKey: [SyncCommandControlQueryKeys.RESTART, sourceId, engineId],
    queryFn: () =>
      SyncCommandControlService.Restart(authorizedFetch(), {
        sourceId: sourceId,
        engineId: engineId,
      }),
    retry: false,
    enabled: false,
    retryOnMount: false,
    refetchOnMount: false,
    cacheTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    ...options,
  });
};

const useGetStatusSyncCommandControl = (sourceId: string, engineId: string) => {
  return useQuery<StatusResponse, Error>({
    queryKey: [SyncCommandControlQueryKeys.STATUS, sourceId, engineId],
    queryFn: () =>
      SyncCommandControlService.GetStatus(authorizedFetch(), {
        sourceId: sourceId,
        engineId: engineId,
      }),
    retry: false,
    enabled: !!sourceId,
    retryOnMount: true,
    refetchOnMount: 'always',
    cacheTime: CACHE_TIME,
    staleTime: CACHE_TIME,
  });
};

export const SyncEngineSourcesQueries = {
  useGetSyncEngineSources,
  useGetSourceContainerConfigs,
};

export const SyncCommandControlQueries = {
  usePingSyncCommandControl,
  useRestartSyncCommandControl,
  useGetStatusSyncCommandControl,
};
